import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  IconButton,
  InputAdornment,
  Fade,
  Slide,
  Zoom,
  Grow,
  keyframes,
  useTheme,
  useMediaQuery,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import czechTranslations from '../../translations/czech';

// 🎨 Animated backgrounds
const floatingAnimation = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-30px) rotate(120deg); }
  66% { transform: translateY(-60px) rotate(240deg); }
  100% { transform: translateY(0px) rotate(360deg); }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.3); }
  50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.6), 0 0 60px rgba(99, 102, 241, 0.4); }
  100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.3); }
`;

const successPulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // 🎭 Animation states
  const [showContent, setShowContent] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  // 📝 Form state
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [canResend, setCanResend] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    // 🎪 Staggered entrance animations
    setTimeout(() => setShowContent(true), 200);
    setTimeout(() => setShowForm(true), 600);
  }, []);

  // Resend timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email) {
      setError(czechTranslations.auth.forgotPassword.errors.emailRequired);
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(czechTranslations.auth.forgotPassword.errors.invalidEmail);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('/password-reset/forgot-password', {
        email: email.trim(),
      });

      if (response.data.success) {
        setSuccess(true);
        setCanResend(false);
        setResendTimer(60); // 60 seconds cooldown
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || czechTranslations.auth.forgotPassword.errors.serverError;
      
      if (err.response?.status === 429) {
        setError(czechTranslations.auth.forgotPassword.errors.tooManyRequests);
      } else {
        setError(errorMessage);
      }
    }

    setLoading(false);
  };

  const handleResend = () => {
    if (canResend) {
      setSuccess(false);
      setError('');
      handleSubmit(new Event('submit') as any);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (error) setError(''); // Clear error when user starts typing
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #6366f1 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%)',
          zIndex: 1,
        }
      }}
    >
      {/* ✨ Floating animated elements */}
      {[...Array(6)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: { xs: '60px', md: '80px' },
            height: { xs: '60px', md: '80px' },
            borderRadius: '50%',
            background: `linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 100%)`,
            animation: `${floatingAnimation} ${6 + i * 2}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`,
            zIndex: 1,
            top: `${20 + Math.random() * 60}%`,
            left: `${10 + Math.random() * 80}%`,
          }}
        />
      ))}

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        <Fade in={showContent} timeout={800}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {/* 🔑 Lock Icon */}
            <Zoom in={showContent} timeout={1000} style={{ transitionDelay: '200ms' }}>
              <Box
                sx={{
                  width: { xs: 80, md: 120 },
                  height: { xs: 80, md: 120 },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  animation: `${pulseGlow} 3s ease-in-out infinite`,
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                {success ? (
                  <CheckCircleIcon 
                    sx={{ 
                      fontSize: { xs: 40, md: 60 }, 
                      color: '#10b981',
                      animation: `${successPulse} 1s ease-in-out infinite`,
                    }} 
                  />
                ) : (
                  <EmailIcon sx={{ fontSize: { xs: 40, md: 60 }, color: 'white' }} />
                )}
              </Box>
            </Zoom>

            {/* 🎨 Animated title */}
            <Typography
              variant="h2"
              sx={{
                color: 'white',
                fontWeight: 800,
                mb: 2,
                background: success 
                  ? 'linear-gradient(45deg, #10b981 30%, #34d399 90%)'
                  : 'linear-gradient(45deg, #ffffff 30%, #f0f9ff 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 0 30px rgba(255,255,255,0.5)',
                transition: 'all 0.5s ease',
              }}
            >
              {success ? czechTranslations.auth.forgotPassword.success.title : czechTranslations.auth.forgotPassword.title}
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)', 
                fontWeight: 400,
                mb: 4,
                transition: 'all 0.5s ease',
              }}
            >
              {success 
                ? czechTranslations.auth.forgotPassword.success.message
                : czechTranslations.auth.forgotPassword.subtitle
              }
            </Typography>
          </Box>
        </Fade>

        <Slide in={showForm} direction="up" timeout={800}>
          <Paper
            elevation={24}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            }}
          >
            {success ? (
              // ✅ Success State
              <Grow in={success} timeout={600}>
                <Box sx={{ textAlign: 'center' }}>
                  <CheckCircleIcon 
                    sx={{ 
                      fontSize: 64, 
                      color: 'success.main', 
                      mb: 2,
                      animation: `${successPulse} 2s ease-in-out infinite`,
                    }} 
                  />
                  
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'success.main' }}>
                    {czechTranslations.auth.forgotPassword.success.title}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    {czechTranslations.auth.forgotPassword.success.instruction}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleResend}
                      disabled={!canResend || loading}
                      startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                      sx={{
                        borderRadius: 3,
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                          transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                          opacity: 0.6,
                        },
                      }}
                    >
                      {canResend 
                        ? czechTranslations.auth.forgotPassword.success.resend
                        : `Odeslat znovu za ${resendTimer}s`
                      }
                    </Button>

                    <Button
                      variant="text"
                      onClick={() => navigate('/login')}
                      startIcon={<ArrowBackIcon />}
                      sx={{
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                    >
                      {czechTranslations.auth.forgotPassword.backToLogin}
                    </Button>
                  </Box>
                </Box>
              </Grow>
            ) : (
              // 📝 Form State
              <Box>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                    {czechTranslations.auth.forgotPassword.title}
                  </Typography>
                </Box>

                {error && (
                  <Grow in={!!error} timeout={500}>
                    <Alert 
                      severity="error" 
                      sx={{ 
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-message': {
                          fontWeight: 500
                        }
                      }}
                    >
                      {error}
                    </Alert>
                  </Grow>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Grow in={showForm} timeout={600} style={{ transitionDelay: '200ms' }}>
                    <TextField
                      fullWidth
                      label={czechTranslations.auth.forgotPassword.email}
                      type="email"
                      value={email}
                      onChange={handleInputChange}
                      margin="normal"
                      required
                      autoFocus
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          transition: 'all 0.3s ease',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 20px rgba(99, 102, 241, 0.2)',
                          },
                        },
                      }}
                    />
                  </Grow>

                  <Grow in={showForm} timeout={600} style={{ transitionDelay: '300ms' }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      sx={{
                        mt: 3,
                        mb: 2,
                        py: 1.5,
                        borderRadius: 3,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                          transition: 'left 0.5s',
                        },
                        '&:hover::before': {
                          left: '100%',
                        },
                        '&:hover': {
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)',
                        },
                        '&:disabled': {
                          background: 'rgba(99, 102, 241, 0.5)',
                          transform: 'none',
                        },
                      }}
                    >
                      {loading ? czechTranslations.common.loading.default : czechTranslations.auth.forgotPassword.sendButton}
                    </Button>
                  </Grow>

                  <Grow in={showForm} timeout={600} style={{ transitionDelay: '400ms' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        variant="text"
                        onClick={() => navigate('/login')}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                          color: 'primary.main',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: 'rgba(99, 102, 241, 0.05)',
                          },
                        }}
                      >
                        {czechTranslations.auth.forgotPassword.backToLogin}
                      </Button>
                    </Box>
                  </Grow>
                </Box>
              </Box>
            )}
          </Paper>
        </Slide>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
